import './App.css';
import React, { useEffect, useState, useMemo, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  createTheme,
  ThemeProvider,
  AppBar,
  Toolbar,
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import InfinityLogo from './components/InfinityLogo';
import DomainChoicePage from './pages/DomainChoicePage';
import * as serviceWorkerRegistration from './utils/serviceWorkerRegistration';
import { initPerformanceMonitoring } from './utils/performanceMonitor';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Lazy load heavy components for better performance
const CodingChallenge = React.lazy(() => import('./pages/CodingChallenge'));
const ProofHeroCarousel = React.lazy(() => import('./components/ProofHeroCarousel'));
const CompactTiers = React.lazy(() => import('./components/CompactTiers'));
const HowItWorksStrip = React.lazy(() => import('./components/HowItWorksStrip'));
const RequestDemoModal = React.lazy(() => import('./components/RequestDemoModal'));
const StartMvpModal = React.lazy(() => import('./components/StartMvpModal'));
const WorkThatShippedModal = React.lazy(() => import('./components/WorkThatShippedModal'));
const LastHumanCoderModal = React.lazy(() => import('./components/LastHumanCoderModal'));
const PrivacyPolicy = React.lazy(() => import('./components/legal/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./components/legal/TermsAndConditions'));
const Footer = React.lazy(() => import('./components/Footer'));
const CustomerSupport = React.lazy(() => import('./components/CustomerSupport'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const SnackEscapePage = React.lazy(() => import('./pages/SnackEscapePage'));
const DayInLifeQA = React.lazy(() => import('./pages/DayInLifeQA'));
const BookLandingPage = React.lazy(() => import('./pages/BookLandingPage'));
const KidsCodeLandingPage = React.lazy(() => import('./pages/KidsCodeLandingPage'));
const StorybookPage = React.lazy(() => import('./pages/StorybookPage'));
const DomainDelegationPage = React.lazy(() => import('./pages/DomainDelegationPage'));
const EngineerPage = React.lazy(() => import('./pages/EngineerPage'));
const TokyoLandingPage = React.lazy(() => import('./pages/TokyoLandingPage'));
const OhioLandingPage = React.lazy(() => import('./pages/OhioLandingPage'));
const EuropeLandingPage = React.lazy(() => import('./pages/EuropeLandingPage'));
const WixExitLandingPage = React.lazy(() => import('./pages/WixExitLandingPage'));
const ChiropractorsOfferPage = React.lazy(() => import('./pages/ChiropractorsOfferPage'));
const CommandBar = React.lazy(() => import('./components/CommandBar'));
const HowToPromptBlogPage = React.lazy(() => import('./pages/HowToPromptBlogPage'));
const May2026TipsPage = React.lazy(() => import('./pages/May2026TipsPage'));
const May2026TipPostPage = React.lazy(() => import('./pages/May2026TipPostPage'));
const ToolsetAIPage = React.lazy(() => import('./pages/ToolsetAIPage'));
const DemosShowcasePage = React.lazy(() => import('./pages/DemosShowcasePage'));
const KargonetixCaseStudyPage = React.lazy(() => import('./pages/KargonetixCaseStudyPage'));
const CareersHubPage = React.lazy(() => import('./pages/CareersHubPage'));
const CareerRolePage = React.lazy(() => import('./pages/CareerRolePage'));
const OnCallPlanPage = React.lazy(() => import('./pages/OnCallPlanPage'));

// Next-Level Global Theme
const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    allVariants: {
      fontWeight: 500,
    },
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#6366f1',
      light: '#a5b4fc',
      dark: '#4338ca',
    },
    secondary: {
      main: '#ec4899',
      light: '#f9a8d4',
      dark: '#be185d',
    },
    background: {
      default: '#0f0f23',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.8)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '12px 24px',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6366f1',
              boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#6366f1',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: 'white',
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
});

// Component to scroll to top when route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch (error) {
      // JSDOM does not implement scrollTo; ignore in tests.
    }
    AOS.refresh();
  }, [pathname]);

  return null;
}

function StaticHtmlRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
}

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [requestDemoOpen, setRequestDemoOpen] = useState(false);
  const [startMvpOpen, setStartMvpOpen] = useState(false);
  const [workShippedOpen, setWorkShippedOpen] = useState(false);
  const [productionsOpen, setProductionsOpen] = useState(false);


  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: 'ease-out-cubic',
      once: true,
      offset: 90,
    });

    // Initialize performance monitoring outside test runs.
    const cleanupMonitoring = process.env.NODE_ENV === 'test' ? () => {} : initPerformanceMonitoring();

    serviceWorkerRegistration.register({
      onUpdate: () => console.log('New content available'),
      onSuccess: () => console.log('Service worker registered successfully'),
    });

    return () => cleanupMonitoring?.();
  }, []);

  const handleContactClick = useCallback(() => {
    setStartMvpOpen(true);
    setMobileMenuOpen(false);
  }, []);

  const navItems = useMemo(() => [
    { label: 'Contact', onClick: handleContactClick, outlined: true, green: true },
    { label: 'Portal', href: 'https://billing.stripe.com/p/login/00g4ji3Gy93U3tKfYY', outlined: true },
  ], [handleContactClick]);

  return (
    <Router>
      <ScrollToTop />
      <ThemeProvider theme={theme}>
        <Suspense fallback={null}>
          <CommandBar />
        </Suspense>
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            background: 'rgba(15, 15, 35, 0.9)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
          }}
        >
          <Toolbar sx={{ 
            justifyContent: 'space-between',
            py: 1,
          }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <InfinityLogo size={52} />
            </Link>
            
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  variant={item.outlined ? 'outlined' : 'text'}
                  size="small"
                  component={item.href ? 'a' : item.to ? Link : 'button'}
                  href={item.href}
                  target={item.href ? '_blank' : undefined}
                  rel={item.href ? 'noopener noreferrer' : undefined}
                  to={item.to}
                  onClick={item.onClick}
                  startIcon={item.outlined ? <InfinityLogo size={16} variant="icon" /> : undefined}
                  sx={{
                    borderColor: item.green
                      ? 'rgba(34,197,94,0.9)'
                      : item.outlined
                        ? 'rgba(168,85,247,0.85)'
                        : 'transparent',
                    color: item.green ? '#dcfce7' : 'white',
                    background: item.green ? 'rgba(34,197,94,0.16)' : 'transparent',
                    textTransform: 'none',
                    fontSize: '0.82rem',
                    padding: '6px 14px',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': item.outlined
                      ? {
                          content: '""',
                          position: 'absolute',
                          inset: -2,
                          borderRadius: '999px',
                          border: item.green ? '1px solid rgba(34,197,94,0.95)' : '1px solid rgba(168,85,247,0.9)',
                          opacity: 0.7,
                          animation: 'outlinePulse 2.4s ease-in-out infinite',
                        }
                      : {},
                    '@keyframes outlinePulse': {
                      '0%, 100%': { opacity: 0.3, boxShadow: '0 0 0 rgba(168,85,247,0.0)' },
                      '50%': { opacity: 1, boxShadow: '0 0 18px rgba(168,85,247,0.65)' },
                    },
                    '&:hover': {
                      borderColor: item.green ? '#22c55e' : item.outlined ? '#6366f1' : 'transparent',
                      background: item.green
                        ? 'rgba(34,197,94,0.24)'
                        : item.outlined
                          ? 'rgba(99, 102, 241, 0.1)'
                          : 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                color: 'white',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '10px',
              }}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              background: 'linear-gradient(180deg, #0f0f23, #1e1e3f)',
              color: 'white',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Navigation
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 1 }} />
            <List>
              {navItems.map((item) => (
                <ListItemButton
                  key={item.label}
                  component={item.href ? 'a' : item.to ? Link : 'button'}
                  href={item.href}
                  target={item.href ? '_blank' : undefined}
                  rel={item.href ? 'noopener noreferrer' : undefined}
                  to={item.to}
                  onClick={item.onClick || (() => setMobileMenuOpen(false))}
                  sx={{
                    borderRadius: '10px',
                    mb: 0.5,
                    '&:hover': { background: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Drawer>
        
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress size={60} />
          </Box>
        }>
          <Routes>
            <Route path="/" element={
              <>
                <div data-aos="fade-up">
                  <ProofHeroCarousel
                    onRequestDemo={() => setRequestDemoOpen(true)}
                    onStartMvp={() => setStartMvpOpen(true)}
                    onViewWork={() => setWorkShippedOpen(true)}
                  />
                </div>
                <div data-aos="fade-up">
                  <CompactTiers />
                </div>
                <Box
                  data-aos="fade-up"
                  sx={{
                    maxWidth: 1000,
                    mx: 'auto',
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 4 },
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: 1.5, display: 'block', textAlign: 'center', mb: 1 }}>
                    Trusted by partners and clients
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    {[
                      { name: 'Gen Assembly', src: '/assets/images/logos/gen-assembly.png' },
                      { name: 'Interapt', src: '/assets/images/logos/interapt.jpg' },
                      { name: 'Microsoft Azure', src: '/assets/images/logos/microsoft_azure.svg', maxHeight: 52 },
                      { name: 'Expensive Internet', textLogo: true },
                    ].map((logo) => (
                      <Box
                        key={logo.name}
                        sx={{
                          background: 'rgba(255,255,255,0.94)',
                          borderRadius: 2,
                          px: 2.5,
                          py: 1.5,
                          height: 78,
                          minWidth: { xs: 150, md: 190 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                      >
                        {logo.textLogo ? (
                          <Typography
                            sx={{
                              color: '#0b1220',
                              fontWeight: 800,
                              letterSpacing: 0.4,
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              textTransform: 'uppercase',
                              textAlign: 'center',
                            }}
                          >
                            Expensive Internet
                          </Typography>
                        ) : (
                          <Box
                            component="img"
                            src={logo.src}
                            alt={logo.name}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: logo.maxHeight || 44,
                              objectFit: 'contain',
                              display: 'block',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: 1.5, display: 'block', textAlign: 'center', mb: 1 }}>
                    Built and deployed on
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 4,
                    }}
                  >
                    {[
                      { name: 'Amazon Web Services', src: '/assets/images/logos/aws.svg', maxHeight: 46 },
                      { name: 'Microsoft Azure', src: '/assets/images/logos/microsoft_azure.svg', maxHeight: 52 },
                      { name: 'Google Cloud', src: '/assets/images/logos/google-cloud.svg', maxHeight: 30 },
                    ].map((logo) => (
                      <Box
                        key={logo.name}
                        sx={{
                          background: 'rgba(255,255,255,0.94)',
                          borderRadius: 2,
                          px: 2.5,
                          py: 1.5,
                          height: 78,
                          minWidth: { xs: 150, md: 190 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                      >
                        <Box
                          component="img"
                          src={logo.src}
                          alt={logo.name}
                          loading="lazy"
                          sx={{
                            maxWidth: '100%',
                            maxHeight: logo.maxHeight || 44,
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
                    Inside InfiniMVP
                  </Typography>
                  <Box
                    component="video"
                    controls
                    preload="metadata"
                    sx={{
                      width: '100%',
                      borderRadius: 3,
                      border: '1px solid rgba(255,255,255,0.16)',
                      background: '#000',
                      display: 'block',
                    }}
                  >
                    <source src="/assets/videos/InfiniMVP.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </Box>
                </Box>
                <div data-aos="fade-up">
                  <HowItWorksStrip onOpenProductions={() => setProductionsOpen(true)} />
                </div>
              </>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/customer-support" element={<CustomerSupport />} />
            <Route path="/snack-escape" element={<SnackEscapePage />} />
            <Route path="/day-in-life-qa" element={<DayInLifeQA />} />
            <Route path="/book" element={<BookLandingPage />} />
            <Route path="/kids" element={<KidsCodeLandingPage />} />
            <Route path="/storybook" element={<StorybookPage />} />
            <Route path="/domain-delegation" element={<DomainDelegationPage />} />
            <Route path="/coding-challenge" element={<CodingChallenge />} />
            <Route path="/engineer" element={<EngineerPage />} />
            <Route path="/tokyo" element={<TokyoLandingPage />} />
            <Route path="/ohio" element={<OhioLandingPage />} />
            <Route path="/europe" element={<EuropeLandingPage />} />
            <Route path="/wix-exit" element={<WixExitLandingPage />} />
            <Route path="/on-call" element={<OnCallPlanPage />} />
            <Route path="/chiropractors" element={<ChiropractorsOfferPage onRequestDemo={() => setRequestDemoOpen(true)} />} />
            <Route path="/domain-choice" element={<DomainChoicePage />} />
            <Route path="/blog/how-to-prompt-better" element={<HowToPromptBlogPage />} />
            <Route path="/tips/may-2026" element={<May2026TipsPage />} />
            <Route path="/blog/may-2026/:slug" element={<May2026TipPostPage />} />
            <Route path="/toolset-ai" element={<ToolsetAIPage />} />
            <Route path="/demos" element={<DemosShowcasePage onRequestDemo={() => setRequestDemoOpen(true)} onStartMvp={() => setStartMvpOpen(true)} />} />
            <Route path="/case-studies/kargonetix" element={<KargonetixCaseStudyPage onRequestDemo={() => setRequestDemoOpen(true)} onStartMvp={() => setStartMvpOpen(true)} />} />
            <Route path="/careers" element={<CareersHubPage />} />
            <Route path="/careers/virtual-assistant" element={<CareerRolePage roleSlug="virtual-assistant" />} />
            <Route path="/careers/interns" element={<CareerRolePage roleSlug="interns" />} />
            <Route path="/careers/sales-reps" element={<CareerRolePage roleSlug="sales-reps" />} />
            <Route path="/careers/sales-commissions" element={<CareerRolePage roleSlug="sales-commissions" />} />
            <Route path="/wormhole-loading" element={<StaticHtmlRedirect to="/wormhole-loading.html" />} />
            <Route path="/wormhole-transition-prototype" element={<StaticHtmlRedirect to="/wormhole-transition-prototype.html" />} />
          </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <RequestDemoModal open={requestDemoOpen} onClose={() => setRequestDemoOpen(false)} />
          <StartMvpModal open={startMvpOpen} onClose={() => setStartMvpOpen(false)} />
          <WorkThatShippedModal open={workShippedOpen} onClose={() => setWorkShippedOpen(false)} />
          <LastHumanCoderModal open={productionsOpen} onClose={() => setProductionsOpen(false)} />
        </Suspense>
        <Suspense fallback={<CircularProgress />}>
          <Footer />
        </Suspense>
      </ThemeProvider>
    </Router>
  );
}

export default App;
